import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiPost } from "../api";
import { useAuth } from "../auth/AuthContext";
import { Spinner } from "../components/Loaders";
import { Icon } from "../components/Icons";
import { MetricCard, PageIntro } from "../components/Visual";

export function Pricing() {
  const { firebaseUser, profile } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const canceled = params.get("canceled") === "1";

  const onUpgrade = async (plan: "pro" | "plus") => {
    setError(null);
    if (!firebaseUser) {
      navigate("/register");
      return;
    }
    setBusy(true);
    try {
      const { url } = await apiPost<{ url: string }>("/api/billing/checkout", { plan });
      window.location.href = url;
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const isPro = profile?.plan === "pro" || profile?.plan === "plus";
  const isPlus = profile?.plan === "plus";

  return (
    <main className="page pricing-page">
      <PageIntro
        align="center"
        eyebrow="Pricing"
        icon="crown"
        title={<>Simple pricing, <span className="grad">charged with mana.</span></>}
        body="Everyone gets the full deck builder. Unlimited removes the daily build cap, and Unlimited Plus opens Bloomy without chat limits."
      />

      {canceled && <div className="form-error">Checkout canceled. Your plan is unchanged.</div>}
      {error && <div className="form-error">{error}</div>}

      <div className="pricing-metrics">
        <MetricCard label="Free" value="3/day" detail="Deck builds" icon="bolt" />
        <MetricCard label="Unlimited" value="$3" detail="per month" icon="crown" />
        <MetricCard label="Plus" value="$20" detail="Bloomy unlimited" icon="magic" />
      </div>

      <div className="pricing-grid pricing-grid--wild">
        <div className="price-card">
          <span className="price-card-kicker"><Icon name="shield" size={16} /> Starter</span>
          <h3>Free</h3>
          <div className="price">$0<small> forever</small></div>
          <ul>
            <li>3 deck builds per day</li>
            <li>Unlimited deck validation</li>
            <li>Rulings lookup</li>
            <li>Every Magic format supported</li>
            <li>Arena import / export</li>
          </ul>
          {firebaseUser ? (
            profile?.plan === "free" ? (
              <button className="secondary" disabled>Current plan</button>
            ) : (
              <button className="secondary" disabled>Downgrade in Account</button>
            )
          ) : (
            <Link to="/register" className="topbar-btn">Create free account</Link>
          )}
        </div>

        <div className="price-card highlight price-card--pro">
          <span className="price-card-kicker"><Icon name="sparkles" size={16} /> Best for brewers</span>
          <h3>Unlimited</h3>
          <div className="price">$3<small> / month</small></div>
          <ul>
            <li><strong>Unlimited</strong> deck builds + analysis</li>
            <li>10 Bloomy messages every 5 hours</li>
            <li>Priority generation queue</li>
            <li>All Free plan features</li>
            <li>Cancel anytime via Stripe</li>
          </ul>
          {isPro ? (
            <button type="button" className="primary" disabled>{isPlus ? "Included in Plus" : "You're on Unlimited"}</button>
          ) : (
            <button type="button" className="primary" onClick={() => onUpgrade("pro")} disabled={busy}>
              {busy ? (
                <>
                  <Spinner className="button-spinner" />
                  Opening checkout...
                </>
              ) : firebaseUser ? (
                "Upgrade to Unlimited"
              ) : (
                "Sign up + upgrade"
              )}
            </button>
          )}
        </div>

        <div className="price-card">
          <span className="price-card-kicker"><Icon name="magic" size={16} /> Bloomy power users</span>
          <h3>Unlimited Plus</h3>
          <div className="price">$20<small> / month</small></div>
          <ul>
            <li><strong>Unlimited</strong> Bloomy chat</li>
            <li>Ask about your cards, decks, and community decks</li>
            <li>All Unlimited plan features</li>
            <li>Cancel anytime via Stripe</li>
          </ul>
          {isPlus ? (
            <button type="button" className="primary" disabled>You&apos;re on Plus</button>
          ) : (
            <button type="button" className="primary" onClick={() => onUpgrade("plus")} disabled={busy}>
              {busy ? (
                <>
                  <Spinner className="button-spinner" />
                  Opening checkout...
                </>
              ) : firebaseUser ? (
                "Upgrade to Plus"
              ) : (
                "Sign up + upgrade"
              )}
            </button>
          )}
        </div>
      </div>

      <p className="hint pricing-note">
        Payments processed securely by Stripe. No card on file for Free users.
      </p>
    </main>
  );
}
